import {
  resolveLocationAppointmentCatalog,
  validateLocationAppointmentCatalogEmbeddedEligibility,
  type LocationAppointmentCatalog,
} from "@/app/booking/adapters/locationAppointmentCatalog";
import { validateSpecialistPackageOnlinePurchase } from "@/app/commerce/packages/packagePurchaseCatalog";
import { validateSpecialistPackageOnlinePurchaseV2 } from "@/app/commerce/packages/packagePurchaseCatalogV2";
import { capturePackageCatalogData } from "@/app/commerce/packages/packageCatalogSnapshot";
import type { PackageCatalogDefinition } from "@/app/commerce/packages/packageCatalogDefinition";
import { getFrontendPackageCatalogV2FixturePublication, type FrontendPackageCatalogV2FixturePublication } from "@/app/commerce/packages/frontendPackageCatalogV2Fixtures";
import {
  getSpecialistPerson,
  resolveSpecialistServiceCategoriesForAssignment,
} from "@/app/specialists/catalog";
import type { SpecialistCatalogCategory } from "@/app/specialists/catalogCategories";
import { resolveSpecialistCatalogCategories } from "@/app/specialists/catalogCategories";
import type { SpecialistWorkplacePresentation } from "@/app/specialists/components/SpecialistWorkplaceCard";
import type {
  SpecialistPackage,
  SpecialistPerson,
} from "@/app/specialists/model";
import {
  getSpecialistPackagesForAssignment,
  resolveSpecialistPackageCategoriesForAssignment,
} from "@/app/specialists/packages";
import { getSpecialistWorkplacePresentation } from "@/app/specialists/profilePresentation";
import { resolveLocationProfileSnapshot } from "@/app/locations/locationProfile";
import {
  bindLocationServicePortfolioImagesToCatalog,
  resolveLocationSnapshotServicePortfolioImages,
  type LocationServicePortfolioImage,
} from "@/app/locations/locationServicePortfolio";

export interface LocationServicesRouteData {
  catalog: LocationAppointmentCatalog;
  person: SpecialistPerson | null;
  packages: readonly PackageCatalogDefinition[];
  serviceCategoryPublication: readonly SpecialistCatalogCategory[];
  packageCategoryPublication: readonly SpecialistCatalogCategory[];
  workplacePresentation: SpecialistWorkplacePresentation | null;
  servicePortfolioImages: readonly LocationServicePortfolioImage[];
}

export interface LocationServicesRouteDataSource {
  resolveAppointmentCatalog(
    locationId: string,
  ): LocationAppointmentCatalog | null;
  getPerson(personId: string): SpecialistPerson | undefined;
  getPackagesForAssignment(
    userLocationId: string,
    locationId: string,
  ): readonly SpecialistPackage[];
  /** Explicit independent preview publication; never appended to the V1 store. */
  getPackageCatalogV2Publication?(
    userLocationId: string,
    locationId: string,
  ): FrontendPackageCatalogV2FixturePublication | null;
  resolveServiceCategories(
    userLocationId: string,
  ): readonly SpecialistCatalogCategory[] | null;
  resolvePackageCategories(
    userLocationId: string,
    locationId: string,
  ): readonly SpecialistCatalogCategory[] | null;
  getWorkplacePresentation(
    catalog: LocationAppointmentCatalog,
  ): SpecialistWorkplacePresentation | null;
  resolveServicePortfolioImages(
    locationId: string,
    catalog: LocationAppointmentCatalog,
  ): readonly LocationServicePortfolioImage[];
}

export const locationServicesRouteDataFixtureSource: LocationServicesRouteDataSource =
  {
    resolveAppointmentCatalog: resolveLocationAppointmentCatalog,
    getPerson: getSpecialistPerson,
    getPackagesForAssignment: getSpecialistPackagesForAssignment,
    getPackageCatalogV2Publication: (userLocationId, locationId) => {
      const publication = getFrontendPackageCatalogV2FixturePublication({ enabled: true });
      return publication?.locationId === locationId && publication.packages.every(item => item.userLocationId === userLocationId)
        ? publication : null;
    },
    resolveServiceCategories: resolveSpecialistServiceCategoriesForAssignment,
    resolvePackageCategories: resolveSpecialistPackageCategoriesForAssignment,
    getWorkplacePresentation: ({ workplace }) =>
      getSpecialistWorkplacePresentation(workplace),
    resolveServicePortfolioImages: (locationId, catalog) => {
      const profile = resolveLocationProfileSnapshot(locationId);
      if (!profile || profile.locationId !== locationId)
        return Object.freeze([]);

      return bindLocationServicePortfolioImagesToCatalog(
        resolveLocationSnapshotServicePortfolioImages(profile),
        {
          catalogVersion: catalog.workplace.catalogVersion,
          locationId: catalog.workplace.locationId,
          services: catalog.services,
        },
      );
    },
  };

function isCanonicalIdentifier(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value === value.trim() &&
    value.length > 0 &&
    value.length <= 160
  );
}

/**
 * Resolves the complete server-owned Location Services document contract.
 * Capability menus and the route itself must consume this same resolver so a
 * missing person or drifting category publication cannot produce a link to a
 * document that will fail closed with `notFound()`.
 */
export function resolveLocationServicesRouteData(
  locationId: string,
  source: LocationServicesRouteDataSource = locationServicesRouteDataFixtureSource,
): LocationServicesRouteData | null {
  try {
    const catalog = source.resolveAppointmentCatalog(locationId);

    if (
      !catalog ||
      catalog.workplace.locationId !== locationId ||
      catalog.catalogAssignment.locationId !== locationId ||
      !validateLocationAppointmentCatalogEmbeddedEligibility(catalog)
    ) {
      return null;
    }

    const resourceOnlyCatalog = catalog.professionals.length === 0 && Boolean(catalog.eligibilityPublication.resourceOnlyExecutionsV1);
    const person = resourceOnlyCatalog ? null : source.getPerson(catalog.catalogAssignment.personId) ?? null;

    if (!resourceOnlyCatalog && (!person || person.personId !== catalog.catalogAssignment.personId)) {
      return null;
    }

    const packageRows = catalog.workplace.offersPackages
      ? source.getPackagesForAssignment(
          catalog.catalogAssignment.userLocationId,
          catalog.workplace.locationId,
        )
      : [];

    if (!Array.isArray(packageRows)) {
      return null;
    }

    const packageValidations = packageRows.map(
      validateSpecialistPackageOnlinePurchase,
    );

    if (packageValidations.some((validation) => !validation.valid)) {
      return null;
    }

    const packages: PackageCatalogDefinition[] = packageValidations.flatMap((validation) =>
      validation.valid ? [validation.packageItem] : [],
    );

    if (
      packages.some(
        (packageItem, index) =>
          !isCanonicalIdentifier(packageItem.packageId) ||
          !isCanonicalIdentifier(packageItem.catalogVersion) ||
          !isCanonicalIdentifier(packageItem.categoryId) ||
          !Number.isSafeInteger(packageItem.order) ||
          packageItem.order < 1 ||
          packageItem.userLocationId !==
            catalog.catalogAssignment.userLocationId ||
          packageItem.locationId !== locationId ||
          packageItem.catalogVersion !== catalog.workplace.catalogVersion ||
          (index > 0 && packages[index - 1]!.order >= packageItem.order),
      ) ||
      new Set(packages.map(({ packageId }) => packageId)).size !==
        packages.length ||
      new Set(packages.map(({ order }) => order)).size !== packages.length ||
      new Set(packages.map(({ catalogVersion }) => catalogVersion)).size > 1 ||
      new Set(
        packages.map(({ onlinePublication }) => onlinePublication.providerId),
      ).size > 1
    ) {
      return null;
    }

    // V1 retains its single-provider/order contract. The separately authored
    // V2 preview has its own complete publication and cannot rewrite those IDs.
    const rawPreview = catalog.workplace.offersPackages && (process.env.NODE_ENV === "development" || process.env.NODE_ENV === "test")
      ? source.getPackageCatalogV2Publication?.(catalog.catalogAssignment.userLocationId, locationId) ?? null
      : null;
    if (rawPreview !== null) {
      const capture = capturePackageCatalogData(rawPreview);
      if (!capture.valid || !capture.value || typeof capture.value !== "object" || Array.isArray(capture.value)) return null;
      const preview = capture.value as FrontendPackageCatalogV2FixturePublication;
      if (Object.keys(preview).length !== 7 || !["schemaVersion", "source", "publicationId", "providerId", "locationId", "catalogVersion", "packages"].every(key => Object.hasOwn(preview, key)) ||
        preview.schemaVersion !== 2 || preview.source !== "frontend-fixture" || !isCanonicalIdentifier(preview.publicationId) ||
        !isCanonicalIdentifier(preview.providerId) || preview.locationId !== locationId || preview.catalogVersion !== catalog.workplace.catalogVersion ||
        !Array.isArray(preview.packages) || preview.packages.length === 0 || preview.packages.length > 50) return null;
      const rows = preview.packages.map(validateSpecialistPackageOnlinePurchaseV2);
      if (rows.some(row => !row.valid)) return null;
      const definitions = rows.flatMap(row => row.valid ? [row.packageItem] : []);
      if (definitions.some((item, index) => item.locationId !== locationId || item.userLocationId !== catalog.catalogAssignment.userLocationId ||
        item.catalogVersion !== preview.catalogVersion || item.onlinePublication.providerId !== preview.providerId ||
        (index > 0 && definitions[index - 1]!.order >= item.order)) ||
        new Set([...packages, ...definitions].map(item => item.packageId)).size !== packages.length + definitions.length) return null;
      packages.push(...definitions);
    }

    const rawServiceCategories = source.resolveServiceCategories(
      catalog.catalogAssignment.userLocationId,
    );
    const rawPackageCategories =
      packages.length === 0
        ? []
        : source.resolvePackageCategories(
            catalog.catalogAssignment.userLocationId,
            catalog.workplace.locationId,
          );
    const serviceCategoryPublication = rawServiceCategories
      ? resolveSpecialistCatalogCategories(
          catalog.services.map(({ categoryId }) => categoryId),
          rawServiceCategories,
        )
      : null;
    const packageCategoryPublication = rawPackageCategories
      ? resolveSpecialistCatalogCategories(
          packages.map(({ categoryId }) => categoryId),
          rawPackageCategories,
        )
      : null;

    if (!serviceCategoryPublication || !packageCategoryPublication) {
      return null;
    }

    let rawServicePortfolioImages: readonly LocationServicePortfolioImage[] =
      [];
    try {
      const resolvedServicePortfolioImages =
        source.resolveServicePortfolioImages(locationId, catalog);
      rawServicePortfolioImages = Array.isArray(resolvedServicePortfolioImages)
        ? resolvedServicePortfolioImages
        : [];
    } catch {
      rawServicePortfolioImages = [];
    }
    const servicePortfolioImages = Array.isArray(rawServicePortfolioImages)
      ? bindLocationServicePortfolioImagesToCatalog(rawServicePortfolioImages, {
          catalogVersion: catalog.workplace.catalogVersion,
          locationId: catalog.workplace.locationId,
          services: catalog.services,
        })
      : Object.freeze([]);

    return {
      catalog,
      person,
      packages,
      serviceCategoryPublication,
      packageCategoryPublication,
      workplacePresentation: source.getWorkplacePresentation(catalog),
      servicePortfolioImages,
    };
  } catch {
    return null;
  }
}
